"use client";

import { Turnstile } from "@marsidev/react-turnstile";
import type { TurnstileInstance } from "@marsidev/react-turnstile";
import { forwardRef, type RefObject } from "react";

interface TurnstileWidgetProps {
  onSuccess?: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  className?: string;
}

const TurnstileWidget = forwardRef<TurnstileInstance, TurnstileWidgetProps>(
  ({ onSuccess, onExpire, onError, className }, ref) => {
    const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

    if (!siteKey) return null;

    return (
      <Turnstile
        ref={ref}
        siteKey={siteKey}
        onSuccess={onSuccess}
        onExpire={onExpire}
        onError={onError}
        className={className}
        options={{
          theme: "light",
          size: "invisible",
          execution: "execute",
        }}
      />
    );
  },
);

TurnstileWidget.displayName = "TurnstileWidget";

export default TurnstileWidget;

// Drives the invisible+execute Turnstile flow: triggers a challenge run and
// resolves to a fresh token, or null if the widget never produces one
// (script blocked, network blocked, user dismissed the challenge, timeout).
export async function executeTurnstile(
  ref: RefObject<TurnstileInstance | null>,
  timeoutMs = 20000,
): Promise<string | null> {
  const deadline = Date.now() + 3000;
  while (!ref.current && Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 50));
  }
  if (!ref.current) return null;

  try {
    ref.current.reset();
    ref.current.execute();
    const token = await ref.current.getResponsePromise(timeoutMs);
    return token || null;
  } catch {
    return null;
  }
}
